/**
 * Multi-currency balances for the active IB account via MobileService account proxy:
 * GET /v1/ib/accounts/balances → BaaS GET /v1/accounts/{activeId}/balances (BalanceList).
 * Foreign-only vs all-currencies is filtered client-side (no BaaS `/balances/web`).
 */

import { fetchAxios } from "../API/fetchAxios";
import { getIbAccountQueryParams } from "../Constants/accountTypes";
import { CURRENCIES } from "../Pages/Private/ContaInternacional/constants";

const metaByCode = CURRENCIES.reduce((map, currency) => {
  map[currency.code.toUpperCase()] = currency;
  return map;
}, {});

const PRIMARY_CODES = new Set(
  CURRENCIES.filter((currency) => currency.isPrimary).map((currency) => currency.code.toUpperCase()),
);

const toMajorUnits = (value) => Number(value || 0) / 100;

/**
 * Map MobileService BalanceModel (snake_case or PascalCase) → Conta internacional card shape.
 * Also accepts compact list rows where `currency` is a string code.
 */
export const mapIbBalanceRow = (row) => {
  const currency =
    typeof row?.currency === "string"
      ? { code: row.currency }
      : row?.currency || {};
  const code = String(
    currency.code || currency.Code || row?.currency_code || row?.currencyCode || "",
  ).toUpperCase();
  if (!code) return null;

  const id = code.toLowerCase();
  const meta = metaByCode[code];

  return {
    id,
    label: currency.name || currency.Name || meta?.label || code,
    code,
    balance: toMajorUnits(row.available ?? row.Available),
    blockedBalance: toMajorUnits(
      row.blocked_balance ??
        row.blockedBalance ??
        row.BlockedBalance ??
        row.blocked ??
        row.Blocked,
    ),
    futureBalance: toMajorUnits(
      row.future ??
        row.Future ??
        row.future_balance ??
        row.futureBalance ??
        row.FutureBalance ??
        row.scheduled_balance ??
        row.scheduledBalance ??
        row.scheduled,
    ),
    limit: toMajorUnits(row.limit ?? row.Limit),
    rateToBrl: meta?.rateToBrl ?? (code === "BRL" ? 1 : 0),
    isPrimary: PRIMARY_CODES.has(code) || (code !== "BRL" && !meta),
    icon: currency.icon || currency.Icon || null,
    culture: currency.culture || currency.Culture || null,
    symbol: currency.symbol || currency.Symbol || meta?.code || code,
    raw: row,
  };
};

const extractBalanceRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const fetchBalancesContext = async (context, accountId) => {
  const response = await fetchAxios({
    verb: "GET",
    context,
    responseType: "",
    queryParameters: getIbAccountQueryParams(accountId),
    cancelToken: undefined,
  });
  return extractBalanceRows(response?.data ?? response)
    .map(mapIbBalanceRow)
    .filter(Boolean)
    .sort((a, b) => {
      if (a.code === "BRL") return 1;
      if (b.code === "BRL") return -1;
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.code.localeCompare(b.code);
    });
};

/** Foreign currency balances for the selected IB account (excludes BRL). */
export const fetchIbForeignBalances = async (accountId) => {
  const rows = await fetchBalancesContext("accountBalances", accountId);
  return rows.filter((row) => row.code !== "BRL");
};

/** All currency balances for the selected IB account (includes BRL). */
export const fetchIbAllCurrencyBalances = (accountId) =>
  fetchBalancesContext("accountBalancesWeb", accountId);
